import { getRequestEvent } from "solid-js/web";
import { getStaticJson } from "~/server/static";
import { CategoryResult } from "~/server/types/category";
import { Product } from "~/server/types/search";
import { HomePageSectionSeparator } from "~/server/types/separator";
import { clientRepo } from "~/server/apis/client_repo";

export type SectionData = {
  title: string;
  bgColor: string;
  brandList?: Product[];
};

export type HomeV3RouteData = {
  categories: CategoryResult;
  brands: Product[];
  sections: SectionData[];
  showSeparators: boolean;
  hasLimitedBrands: () => boolean;
};

export async function getHomeV3RouteData(props: {
  fetchHomeSeparatorSections: boolean;
}): Promise<HomeV3RouteData> {
  const { fetchHomeSeparatorSections } = props;
  const requestEvent = getRequestEvent();
  const clientId = requestEvent?.locals.clientId;

  const [categories, allBrands] = await Promise.all([
    clientRepo.getCategories(),
    clientRepo.getProductSearch({ query: "" }),
  ]);

  const _showSeparators = (): boolean => {
    return allActiveBrandProducts.length > 50;
  };

  const allActiveBrandProducts =
    allBrands?.data?.[0]?.products?.filter(
      (product) => product.voucherProduct.status === "ACTIVE"
    ) || [];

  const showSeparators = _showSeparators();

  // Only fetch sections if requested and we have enough brands
  let sections: SectionData[] = [];

  if (fetchHomeSeparatorSections && showSeparators) {
    const homePageSectionSeparator = (await getStaticJson(
      "home_page/home_separators.json"
    )) as HomePageSectionSeparator;

    let separators = homePageSectionSeparator.separators;

    // for raisoni portal, filter out 'newly added brands' section
    if (clientId?.startsWith("raisoni")) {
      separators = separators.filter((separator) => separator.id !== "1");
    }

    sections = separators
      .map((separator) => {
        const products = separator.brands
          .map((brandKey) =>
            allBrands?.data?.[0]?.products?.find((p) => p.brandKey === brandKey)
          )
          .filter((product): product is Product =>
            Boolean(product && product.voucherProduct.status === "ACTIVE")
          );

        return {
          title: separator.heading,
          bgColor: separator.color_code,
          brandList: products,
        };
      })
      .filter((section) => (section.brandList?.length ?? 0) >= 3);
  }

  const hasLimitedBrands = (): boolean => {
    return allActiveBrandProducts.length < 20;
  };

  return {
    categories,
    brands: allActiveBrandProducts,
    sections,
    showSeparators,
    hasLimitedBrands,
  };
}
